import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Mail, Download } from "lucide-react";
import { useApi, useFetch, errMsg, inr } from "../lib/api";
import { LicenceTools } from "./LicenceTools";

const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const tone = (x) => (x.days_left < 0 ? "bg-rose-500" : x.days_left <= 30 ? "bg-amber-400" : "bg-[#00E599]");
const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const FeeTotals = ({ fees }) => {
  if (!fees) return null;
  const cur = fees.by_fy.find((x) => x.fy === fees.current_fy);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="renewal-fee-totals">
      <span className="text-slate-400">Renewal fees {fees.current_fy}:</span>
      <span className="font-mono text-white" data-testid="renewal-fee-current">{inr(cur?.total || 0)}</span>
      {fees.by_fy.filter((x) => x.fy !== fees.current_fy).slice(0, 3).map((x) => (
        <span key={x.fy} className="rounded-full border border-[#1F2A3E] px-2 py-0.5 text-slate-400" data-testid="renewal-fee-fy">{x.fy}: <span className="font-mono">{inr(x.total)}</span></span>))}
      {fees.budget && <BudgetChip b={fees.budget} />}
    </div>);
};

const BudgetChip = ({ b }) => {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const download = async () => {
    try {
      const r = await api.get("/licenses/fees/budget.xlsx", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `renewal-budget-${b.fy.replace(" ", "-")}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <span className="relative z-30">
      <button onClick={() => setOpen(!open)} data-testid="fee-budget-btn" className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-cyan-300 hover:border-cyan-400">
        Budget {b.fy}: <span className="font-mono" data-testid="fee-budget-total">{inr(b.total)}</span>{b.unknown > 0 && ` + ${b.unknown} without past fee`}</button>
      {open && <div className="absolute z-50 left-0 mt-2 w-80 rounded-lg border border-[#1F2A3E] bg-[#121824] p-3 shadow-xl grid grid-cols-[minmax(0,1fr)] gap-1.5" data-testid="fee-budget-list">
        <div className="text-[11px] text-slate-500">Licences expiring in {b.fy}, estimated at the fee last paid for each.</div>
        {b.items.map((x) => (
          <div key={`${x.kind}${x.id}`} className="flex min-w-0 justify-between gap-2 text-xs" data-testid="fee-budget-item"><span className="truncate min-w-0">{x.label} <span className="text-slate-500 font-mono">{x.expiry_date}</span></span>
            <span className="font-mono shrink-0">{x.estimate == null ? "no past fee" : inr(x.estimate)}</span></div>))}
        {!b.items.length && <div className="text-xs text-slate-400">No licences expire in {b.fy}.</div>}
        <button onClick={download} data-testid="fee-budget-download" className="mt-1 flex items-center justify-center gap-1.5 rounded-md bg-[#00E599] px-2 py-1.5 text-xs font-semibold text-[#0B1017] hover:bg-[#00C482]"><Download size={13} />Download Excel</button>
      </div>}
    </span>);
};

const Grid = ({ month, items, pick, picked }) => {
  const [y, m] = month.split("-").map(Number);
  const lead = (new Date(y, m - 1, 1).getDay() + 6) % 7, total = new Date(y, m, 0).getDate();
  const n = new Date(), today = `${ym(n)}-${String(n.getDate()).padStart(2, "0")}`;
  const cells = [...Array(lead).fill(null), ...Array.from({ length: total }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`)];
  return (
    <div className="grid grid-cols-7 gap-1 text-center text-xs min-w-0 [&>*]:min-w-0" data-testid="licence-calendar-grid">
      {WEEK.map((w) => <div key={w} className="text-[10px] text-slate-500 py-1">{w}</div>)}
      {cells.map((d, i) => {
        const hits = d ? items.filter((x) => x.expiry_date === d) : [];
        return d ? (
          <button key={d} onClick={() => pick(hits.length ? d : null)} data-testid={hits.length ? "licence-calendar-day-due" : undefined}
            className={`h-9 rounded-md flex flex-col items-center justify-center transition-colors ${picked === d ? "bg-[#00E599]/15 ring-1 ring-[#00E599]" : "hover:bg-white/5"} ${d === today ? "font-bold text-white" : "text-slate-300"}`}>
            <span className="font-mono">{Number(d.slice(8))}</span>
            {hits.length > 0 && <span className="flex gap-0.5 mt-0.5">{hits.slice(0, 3).map((x) => <span key={x.id} className={`h-1.5 w-1.5 rounded-full ${tone(x)}`} />)}</span>}
          </button>) : <div key={`b${i}`} />;
      })}
    </div>);
};

export const LicenceCalendar = () => {
  const api = useApi();
  const { data, reload: reloadCal } = useFetch("/licenses/calendar");
  const { data: fees, reload: reloadFees } = useFetch("/licenses/fees");
  const reload = () => { reloadCal(); reloadFees(); };
  const [month, setMonth] = useState(ym(new Date()));
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const shift = (n) => { const [y, m] = month.split("-").map(Number); setMonth(ym(new Date(y, m - 1 + n, 1))); setPicked(null); };
  const items = data || [];
  const list = items.filter((x) => (picked ? x.expiry_date === picked : x.expiry_date.startsWith(month)));
  const digest = async () => {
    setBusy(true);
    try { const r = (await api.post("/licenses/digest")).data; if (!r.items) toast.success("Nothing expires in the next 90 days");
      else if (!r.sent) toast.error("Digest could not be emailed right now. Please try again in a minute");
      else toast.success(`Digest emailed (${r.items} licence(s), ${r.sent} email(s)${r.contractor_sent ? `, ${r.contractor_sent} to contractors` : ""})`); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="c-surface p-6 c-rise mt-6" data-testid="licence-calendar-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div><div className="c-eyebrow mb-1">Licence renewals</div>
          <div className="text-sm text-slate-400"><span className="font-mono text-white" data-testid="licence-due-90">{items.filter((x) => x.days_left <= 90).length}</span> due in the next 90 days</div>
          <FeeTotals fees={fees} /></div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={digest} disabled={busy} data-testid="licence-digest-send-btn" className="flex items-center gap-1.5 rounded-lg border border-[#1F2A3E] px-3 py-1.5 text-xs text-slate-300 hover:text-white hover:border-[#00E599]/40 disabled:opacity-50"><Mail size={13} />Email digest now</button>
          <span className="flex items-center"><button onClick={() => shift(-1)} className="p-1.5 text-slate-400 hover:text-white" data-testid="licence-calendar-prev"><ChevronLeft size={16} /></button>
          <span className="text-sm font-semibold w-32 text-center" data-testid="licence-calendar-month">{new Date(`${month}-01`).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</span>
          <button onClick={() => shift(1)} className="p-1.5 text-slate-400 hover:text-white" data-testid="licence-calendar-next"><ChevronRight size={16} /></button></span>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6 [&>*]:min-w-0">
        <Grid month={month} items={items} pick={setPicked} picked={picked} />
        <div className="grid gap-2 content-start" data-testid="licence-calendar-list">
          {list.map((x) => (
            <div key={`${x.kind}${x.id}`} data-testid="licence-calendar-item"
              className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border border-[#1F2A3E] bg-[#0B1017] px-3 py-2 text-sm hover:border-[#00E599]/40 min-w-0">
              <Link to={x.kind === "clra" ? "/vendors" : "/settings?tab=licenses"} className="flex items-center gap-2 min-w-0 hover:text-white">
                <span className={`h-2 w-2 shrink-0 rounded-full ${tone(x)}`} /><span className="truncate">{x.label}</span><span className="text-[11px] text-slate-500 font-mono">{x.number}</span></Link>
              <span className="flex items-center gap-1 shrink-0"><span className="text-xs font-mono text-slate-400">{x.expiry_date} · {x.days_left < 0 ? "expired" : `${x.days_left}d`}</span>
                <LicenceTools lic={x} reload={reload} base={x.kind === "clra" ? `/vendors/${x.id}/clra` : `/licenses/${x.id}`} /></span>
            </div>))}
          {!list.length && <p className="text-xs text-slate-500">No licences expire {picked ? "on this day" : "this month"}. Next: {items.find((x) => x.days_left >= 0)?.expiry_date || "—"}</p>}
          <p className="text-[11px] text-slate-500 mt-1">A digest of everything due in 90 days is emailed every Monday at 9 AM. Each contractor also gets their own CLRA reminder.</p>
        </div>
      </div>
    </div>);
};
